"use client"

import { usePrivy } from "@privy-io/react-auth"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useMemo, useRef } from "react"
import {
  type CheckDisclaimerAcceptanceResponse,
  GACHA_DISCLAIMER_VERSION,
  acceptGachaDisclaimer,
  createGachaDisclaimerAcceptanceQueryOptions,
  getGachaDisclaimerAcceptanceQueryKey,
  getGachaDisclaimerAcceptanceUserQueryKey,
  getGachaDisclaimerNetwork,
  toAcceptedDisclaimerCheckResponse,
} from "./gacha-disclaimer-acceptance"
import { useGachaApiOptions } from "./use-gacha-api-options"
import { useGachaSolanaConfig } from "./use-gacha-solana-config"

export type GachaDisclaimerAcceptanceStatus =
  | "idle"
  | "loading"
  | "accepted"
  | "not-accepted"
  | "error"

export function useGachaDisclaimerAcceptance() {
  const { authenticated, getAccessToken, ready, user } = usePrivy()
  const apiOptions = useGachaApiOptions()
  const { cluster } = useGachaSolanaConfig()
  const queryClient = useQueryClient()
  const privyId = user?.id ?? ""
  const previousPrivyIdRef = useRef(privyId)
  const network = getGachaDisclaimerNetwork(cluster)
  const queryEnabled = ready && authenticated && Boolean(privyId)

  useEffect(() => {
    const previousPrivyId = previousPrivyIdRef.current
    if (previousPrivyId && previousPrivyId !== privyId) {
      queryClient.removeQueries({
        queryKey: getGachaDisclaimerAcceptanceUserQueryKey(previousPrivyId),
      })
    }

    previousPrivyIdRef.current = privyId
  }, [privyId, queryClient])

  const acceptanceQueryOptions = useMemo(
    () =>
      createGachaDisclaimerAcceptanceQueryOptions({
        enabled: queryEnabled,
        privyId,
        disclaimerVersion: GACHA_DISCLAIMER_VERSION,
        network,
        getAccessToken,
        options: apiOptions,
      }),
    [apiOptions, getAccessToken, network, privyId, queryEnabled],
  )
  const acceptanceQuery = useQuery(acceptanceQueryOptions)
  const acceptanceQueryKey = getGachaDisclaimerAcceptanceQueryKey({
    privyId,
    disclaimerVersion: GACHA_DISCLAIMER_VERSION,
    network,
  })
  const acceptanceMutation = useMutation({
    mutationFn: async (walletAddress: string) => {
      const accessToken = await getAccessToken()
      if (!accessToken) {
        throw new Error("Privy access token is unavailable")
      }

      return acceptGachaDisclaimer(
        {
          accessToken,
          walletAddress,
          disclaimerVersion: GACHA_DISCLAIMER_VERSION,
          network,
        },
        apiOptions,
      )
    },
    onSuccess: (response) => {
      queryClient.setQueryData(
        acceptanceQueryKey,
        toAcceptedDisclaimerCheckResponse(response),
      )
    },
  })

  const ensureAcceptanceChecked = useCallback(async () => {
    if (!queryEnabled) {
      throw new Error("Privy authentication is not ready")
    }

    return queryClient.fetchQuery(acceptanceQueryOptions)
  }, [acceptanceQueryOptions, queryClient, queryEnabled])

  const acceptDisclaimer = useCallback(
    (walletAddress: string) => acceptanceMutation.mutateAsync(walletAddress),
    [acceptanceMutation],
  )

  let status: GachaDisclaimerAcceptanceStatus = "idle"
  if (queryEnabled && acceptanceQuery.isPending) {
    status = "loading"
  } else if (acceptanceQuery.isError) {
    status = "error"
  } else if (acceptanceQuery.data?.accepted === true) {
    status = "accepted"
  } else if (acceptanceQuery.data?.accepted === false) {
    status = "not-accepted"
  }

  return {
    status,
    acceptance: acceptanceQuery.data ?? null,
    ensureAcceptanceChecked:
      ensureAcceptanceChecked as () => Promise<CheckDisclaimerAcceptanceResponse>,
    acceptDisclaimer,
    isAccepting: acceptanceMutation.isPending,
    error: acceptanceMutation.error ?? acceptanceQuery.error ?? null,
  }
}
